// Authenticated ticket follow-up: status, public comment thread, attachments
// and a reply box. Requires user to be logged in.
//
// También es donde quien abrió el ticket lo CIERRA (#729): «Cerrado» ya no lo
// pone el equipo sino el solicitante, y con 3 resueltos sin cerrar no puede
// abrir otro — el formulario de /soporte redirige aquí con `?pendientes=1`.
import { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { brand, brandTextColor } from "../../../config/theme";
import { useTheme } from "../../../context/ThemeContext";
import { useUserStore } from "../../../store/userStore";
import { useAlert } from "../../../context/AlertContext";
import { authenticateUser } from "../../../pages/login/functions";
import {
  getPublicTicket,
  addPublicComment,
  getMyTickets,
  getPendingClosure,
  closeMyTicket,
  requestTicketRework,
  formatDate,
  type Ticket,
  type Comment,
  type PendingClosure,
} from "../../../Layouts/service-desk/services/serviceDeskService";
import { SlaBadge, PriorityBadge, ReworkChip } from "../../../Layouts/service-desk/components/Badges";
import { apiErrorMessage } from "../../../Layouts/service-desk/services/agentsPanel";
import {
  isValidReworkReason,
  shouldShowPendingClosureAlert,
  statusFallback,
  CLOSED_STATUS,
  MIN_REWORK_REASON_LENGTH,
  PENDING_CLOSURE_STATUS,
} from "../../../Layouts/service-desk/services/ticketClosure";
import { CommentThread } from "../../../Layouts/service-desk/components/CommentThread";
import { MODULE_OPTIONS } from "../../../Layouts/service-desk/moduleOptions";

export default function SoporteSeguimiento() {
  const { t } = useTranslation(["login", "common"]);
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { user } = useUserStore();
  const { showAlert } = useAlert();
  const [searchParams, setSearchParams] = useSearchParams();
  const ref = searchParams.get("ref") || "";
  // Llegó redirigido desde el formulario de alta por tener tickets sin cerrar.
  const redirectedForPending = searchParams.get("pendientes") === "1";

  const [lookup, setLookup] = useState({ ref });
  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [showGuestModal, setShowGuestModal] = useState(!user);
  const [guestUsername, setGuestUsername] = useState("");
  const [guestPassword, setGuestPassword] = useState("");
  const [guestModalError, setGuestModalError] = useState<string | null>(null);
  const [authenticating, setAuthenticating] = useState(false);
  const [myTickets, setMyTickets] = useState<Ticket[]>([]);
  const [loadingMyTickets, setLoadingMyTickets] = useState(false);
  // Tickets resueltos que aún no cierra. null = sin dato (no cargó o falló):
  // en ese caso no se avisa ni se bloquea nada, el backend decide al crear.
  const [pending, setPending] = useState<PendingClosure | null>(null);
  const [closingRef, setClosingRef] = useState<string | null>(null);
  const [pendingError, setPendingError] = useState<string | null>(null);
  // Acciones de cierre en el detalle de un ticket.
  const [closureMode, setClosureMode] = useState<"idle" | "rework" | "confirmClose">("idle");
  const [reworkReason, setReworkReason] = useState("");
  const [closureBusy, setClosureBusy] = useState(false);
  const [closureError, setClosureError] = useState<string | null>(null);

  const handleGuestSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setGuestModalError(null);
    if (!guestUsername || !guestPassword) {
      setGuestModalError(t("serviceDesk:publicPortal.emptyFieldsError"));
      return;
    }
    setAuthenticating(true);
    try {
      await authenticateUser(
        guestUsername,
        guestPassword,
        navigate,
        showAlert,
        t,
        false // No redirigir
      );
      // authenticateUser maneja sus errores internamente y no lanza excepciones:
      // verificar en el store si la sesión realmente se creó.
      if (useUserStore.getState().user) {
        // Si la autenticación fue exitosa, el useEffect se encargará de cargar el ticket
        setShowGuestModal(false);
      } else {
        setGuestModalError(t("serviceDesk:publicPortal.invalidCredentialsError"));
      }
    } catch {
      setGuestModalError(t("serviceDesk:publicPortal.invalidCredentialsError"));
    } finally {
      setAuthenticating(false);
    }
  };

  // `silent`: refresca un ticket que YA está en pantalla (tras cerrar o
  // rehacer) sin cambiarlo por «Cargando…», y si la recarga falla se queda con
  // lo que hay: la acción sí se hizo, y «No encontramos el ticket» lo negaría.
  const load = useCallback(async (r: string, { silent = false }: { silent?: boolean } = {}) => {
    if (!r) return;
    if (!silent) {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await getPublicTicket(r);
      setTicket(data.ticket);
      setComments(data.comments);
    } catch {
      if (silent) return;
      setError("No encontramos el ticket. Verifica la referencia.");
      setTicket(null);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => {
    // Verificar autenticación antes de cargar el ticket
    if (!user) {
      setAuthChecked(true);
      return;
    }

    setAuthChecked(true);
    if (ref) load(ref);
  }, [ref, load, user]);

  const loadMyTickets = useCallback(
    () =>
      getMyTickets()
        .then(setMyTickets)
        .catch(() => setMyTickets([])),
    [],
  );

  const loadPending = useCallback(
    () =>
      getPendingClosure()
        .then(setPending)
        .catch(() => setPending(null)),
    [],
  );

  // Load user's tickets when authenticated
  useEffect(() => {
    if (user && !ref) {
      setLoadingMyTickets(true);
      loadMyTickets().finally(() => setLoadingMyTickets(false));
      loadPending();
    }
  }, [user, ref, loadMyTickets, loadPending]);

  // Al cambiar de ticket no debe quedar abierto el motivo ni el error del anterior.
  useEffect(() => {
    setClosureMode("idle");
    setReworkReason("");
    setClosureError(null);
  }, [ref]);

  // Cerrar desde el aviso de pendientes: es la misma confirmación que «Sí,
  // cerrar ticket» del detalle, sin tener que abrir cada uno.
  const closeFromList = async (reference: string) => {
    setPendingError(null);
    setClosingRef(reference);
    try {
      await closeMyTicket(reference);
      await Promise.all([loadPending(), loadMyTickets()]);
    } catch (err: unknown) {
      setPendingError(apiErrorMessage(err) || "No se pudo cerrar el ticket.");
    } finally {
      setClosingRef(null);
    }
  };

  const runClosureAction = async (action: () => Promise<Ticket>, fallback: string) => {
    setClosureError(null);
    setClosureBusy(true);
    try {
      const updated = await action();
      if (updated) setTicket(updated);
      setClosureMode("idle");
      setReworkReason("");
      // El backend puede dejar constancia en la conversación (p. ej. el motivo
      // del retrabajo): se recarga el hilo además de pintar el ticket devuelto.
      // También la cuenta de pendientes: al volver a la lista, el aviso
      // seguía mostrando el ticket recién cerrado y «Crear ticket» bloqueado.
      await Promise.all([load(ref, { silent: true }), loadPending()]);
    } catch (err: unknown) {
      setClosureError(apiErrorMessage(err) || fallback);
    } finally {
      setClosureBusy(false);
    }
  };

  const onCloseTicket = () => runClosureAction(() => closeMyTicket(ref), "No se pudo cerrar el ticket.");
  const onRequestRework = () => {
    if (!isValidReworkReason(reworkReason)) {
      setClosureError(`Cuéntanos qué no quedó resuelto (mínimo ${MIN_REWORK_REASON_LENGTH} caracteres).`);
      return;
    }
    return runClosureAction(() => requestTicketRework(ref, reworkReason.trim()), "No se pudo enviar el ticket a rehacer.");
  };

  const submitLookup = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams({ ref: lookup.ref.trim() });
  };

  const onReply = async (body: string, files: File[]) => {
    await addPublicComment(ref, body, files);
    await load(ref);
  };

  // Check authentication
  if (!authChecked) {
    return <div className={`py-16 text-center ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>Cargando…</div>;
  }

  // Show authentication modal
  if (showGuestModal) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className={`w-full max-w-md rounded-2xl border p-8 shadow-lg ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}`}>
          <h2 className={`mb-2 text-2xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{t("serviceDesk:publicPortal.verifyAccount")}</h2>
          <p className={`mb-6 text-sm ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>
            {t("serviceDesk:publicPortal.verifyAccountDescription")}
          </p>

          {guestModalError && (
            <div className={`mb-4 rounded-lg px-4 py-2 text-sm ${isDarkMode ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-700"}`}>{guestModalError}</div>
          )}

          <form onSubmit={handleGuestSubmit} className="space-y-4">
            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>{t("serviceDesk:publicPortal.usernameOrEmail")} *</label>
              <input
                type="text"
                required
                className={isDarkMode ? darkInputCls : lightInputCls}
                value={guestUsername}
                onChange={(e) => setGuestUsername(e.target.value)}
                placeholder={t("serviceDesk:publicPortal.usernameOrEmail")}
                autoFocus
              />
            </div>

            <div>
              <label className={`block text-xs font-medium mb-1 ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>{t("serviceDesk:publicPortal.password")} *</label>
              <input
                type="password"
                required
                className={isDarkMode ? darkInputCls : lightInputCls}
                value={guestPassword}
                onChange={(e) => setGuestPassword(e.target.value)}
                placeholder={t("serviceDesk:publicPortal.password")}
              />
            </div>

            <button
              type="submit"
              disabled={authenticating}
              className="w-full rounded-full py-3 text-sm font-semibold text-white disabled:opacity-60"
              style={{ backgroundColor: brand.primary }}
            >
              {authenticating ? t("serviceDesk:publicPortal.verifyingButton") : t("serviceDesk:publicPortal.verifyButton")}
            </button>
          </form>
        </div>
      </div>
    );
  }

  // No params yet → show the lookup form and user's tickets if authenticated.
  if (!ref) {
    const createBlocked = Boolean(pending?.blocked);
    const showPendingAlert = shouldShowPendingClosureAlert(pending, redirectedForPending);
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        {showPendingAlert && pending && (
          <div
            role="alert"
            className={`rounded-2xl border-2 p-6 shadow-sm ${isDarkMode ? "border-amber-500/60 bg-amber-500/10" : "border-amber-400 bg-amber-50"}`}
          >
            <h2 className={`text-lg font-bold ${isDarkMode ? "text-amber-200" : "text-amber-900"}`}>
              {createBlocked
                ? "Cierra tus tickets resueltos para poder crear uno nuevo"
                : "Tienes tickets resueltos sin cerrar"}
            </h2>
            <p className={`mt-1 text-sm ${isDarkMode ? "text-amber-100/90" : "text-amber-800"}`}>
              {createBlocked
                ? `Tienes ${pending.count} tickets resueltos sin cerrar y el máximo es ${pending.limit}. Cierra los que ya quedaron resueltos; si alguno no quedó, ábrelo y márcalo para rehacer.`
                : `Te ${pending.count === 1 ? "queda 1 ticket resuelto" : `quedan ${pending.count} tickets resueltos`} por cerrar. Al llegar a ${pending.limit} no podrás crear tickets nuevos.`}
            </p>
            {pendingError && (
              <div className={`mt-3 rounded-lg px-3 py-2 text-sm ${isDarkMode ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-700"}`}>{pendingError}</div>
            )}
            <ul className="mt-4 space-y-2">
              {pending.tickets.map((p) => (
                <li
                  key={p.reference}
                  className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border p-3 ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-amber-200 bg-white"}`}
                >
                  <div className="min-w-0">
                    <div className={`text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{p.reference}</div>
                    <div className={`truncate text-sm ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>{p.title}</div>
                    <div className={`text-xs ${isDarkMode ? "text-white/60" : "text-gray-500"}`}>{p.resolvedAt ? `Resuelto el ${formatDate(p.resolvedAt)}` : "Resuelto"}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={() => setSearchParams({ ref: p.reference })}
                      className={`text-sm font-medium underline ${isDarkMode ? "text-white/80" : "text-gray-700"}`}
                    >
                      Ver ticket
                    </button>
                    <button
                      type="button"
                      onClick={() => closeFromList(p.reference)}
                      disabled={closingRef !== null}
                      className="rounded-full px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
                      style={{ backgroundColor: brand.primary }}
                    >
                      {closingRef === p.reference ? "Cerrando…" : "Cerrar ticket"}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className={`rounded-2xl border p-6 shadow-sm ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}`}>
          <h1 className={`mb-1 text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Seguir un ticket</h1>
          <p className={`mb-4 text-sm ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>Ingresa tu número de referencia.</p>
          <form onSubmit={submitLookup} className="space-y-3">
            <input className={isDarkMode ? darkInputCls : lightInputCls} placeholder="Referencia (ej. SD-AB12CD)" value={lookup.ref} onChange={(e) => setLookup((l) => ({ ...l, ref: e.target.value }))} />
            <div className="flex gap-3">
              <button type="submit" className="flex-1 rounded-full py-2.5 text-sm font-semibold text-white" style={{ backgroundColor: brand.primary }}>
                Buscar ticket
              </button>
              <button
                type="button"
                onClick={() => navigate("/soporte")}
                disabled={createBlocked}
                title={createBlocked ? "Cierra tus tickets resueltos para poder crear uno nuevo" : undefined}
                className="flex-1 rounded-full py-2.5 text-sm font-semibold text-white border-2 disabled:cursor-not-allowed disabled:opacity-50"
                style={{ backgroundColor: "transparent", borderColor: brand.primary, color: brandTextColor }}
              >
                Crear ticket
              </button>
            </div>
          </form>
        </div>

        {user && (
          <div className={`rounded-2xl border p-6 shadow-sm ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}`}>
            <h1 className={`mb-4 text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>Mis tickets</h1>
            {loadingMyTickets ? (
              <div className={`py-8 text-center ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>Cargando tus tickets…</div>
            ) : myTickets.length === 0 ? (
              <div className={`py-8 text-center ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>No tienes tickets creados</div>
            ) : (
              <div className="space-y-3">
                {myTickets.map((t) => (
                  <button
                    key={t.id}
                    onClick={() => setSearchParams({ ref: t.reference })}
                    className={`w-full rounded-lg border p-4 text-left transition-colors ${isDarkMode ? "border-dark-border bg-dark-border hover:bg-dark-hover text-white" : "border-gray-200 bg-gray-50 hover:bg-gray-100"}`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className={`font-semibold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{t.reference}</div>
                        <div className={`mt-1 text-sm ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>{t.title}</div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-white" style={{ backgroundColor: statusFallback(t.status)?.color || brand.primary }}>
                          {t.statusLabel || statusFallback(t.status)?.label || t.status}
                        </span>
                        <ReworkChip count={t.rework?.count} />
                        <PriorityBadge priority={t.priority} />
                      </div>
                    </div>
                    <div className={`mt-2 text-xs ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>
                      Creado el {formatDate(t.createdAt)}
                      {t.status === PENDING_CLOSURE_STATUS && <span className="font-semibold text-amber-600"> · Resuelto: falta que lo cierres</span>}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  if (loading) return <div className={`py-16 text-center ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>Cargando…</div>;
  if (error) return <div className={`rounded-lg px-4 py-3 text-sm ${isDarkMode ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-700"}`}>{error}</div>;
  if (!ticket) return null;

  const getModuleLabel = (moduleId?: string) => MODULE_OPTIONS.find(m => m.id === moduleId)?.label || moduleId;
  const getModuleSectionLabel = (moduleId?: string, sectionId?: string) => {
    const mod = MODULE_OPTIONS.find(m => m.id === moduleId);
    return mod?.sections.find(s => s.id === sectionId)?.label || sectionId;
  };

  const secondaryBtnCls = `rounded-full border px-4 py-2 text-sm font-semibold disabled:opacity-60 ${isDarkMode ? "border-dark-border text-white hover:bg-dark-hover" : "border-gray-300 text-gray-700 hover:bg-gray-50"}`;

  return (
    <div className="space-y-6">
      <button
        type="button"
        onClick={() => setSearchParams({})}
        className={`text-sm hover:underline ${isDarkMode ? "text-white/70" : "text-gray-500"}`}
      >
        ← Mis tickets
      </button>
      <div className={`rounded-2xl border p-6 shadow-sm ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}`}>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className={`rounded-lg px-2.5 py-0.5 text-sm font-bold tracking-wide ${isDarkMode ? "bg-dark-border text-white" : "bg-gray-100 text-gray-700"}`}>{ticket.reference}</span>
          <span className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-white" style={{ backgroundColor: statusFallback(ticket.status)?.color || brand.primary }}>{ticket.statusLabel || statusFallback(ticket.status)?.label || ticket.status}</span>
          <ReworkChip count={ticket.rework?.count} />
          <PriorityBadge priority={ticket.priority} />
          <SlaBadge state={ticket.sla?.overall} />
          {ticket.sla?.paused && <span className="text-xs text-amber-600">SLA en pausa</span>}
        </div>
        <h1 className={`text-xl font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>{ticket.title}</h1>
        <p className={`mt-1 text-xs ${isDarkMode ? "text-white/70" : "text-gray-400"}`}>
          {ticket.brandLabel} · Creado el {formatDate(ticket.createdAt)} · Última actualización {formatDate(ticket.updatedAt)}
        </p>
        {(ticket.module || ticket.moduleSection) && (
          <div className={`mt-3 rounded-lg px-3 py-2 ${isDarkMode ? "bg-dark-border" : "bg-gray-50"}`}>
            <p className={`text-xs font-medium ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>
              <span className="font-semibold">Módulo afectado:</span> {getModuleLabel(ticket.module)}
              {ticket.moduleSection && <span> → {getModuleSectionLabel(ticket.module, ticket.moduleSection)}</span>}
            </p>
          </div>
        )}
        {ticket.description && <p className={`mt-4 whitespace-pre-wrap text-sm ${isDarkMode ? "text-white/70" : "text-gray-700"}`}>{ticket.description}</p>}
        {ticket.githubIssue?.htmlUrl && (
          <a href={ticket.githubIssue.htmlUrl} target="_blank" rel="noreferrer" className={`mt-3 inline-block text-xs font-medium underline ${isDarkMode ? "text-white/70" : "text-gray-500"}`}>
            Issue de GitHub vinculado ({ticket.githubIssue.state})
          </a>
        )}
      </div>

      {/* Cierre por solicitante (#729). En «Resuelto» es LA pregunta de la
          pantalla: confirmar o mandar a rehacer. En cualquier otro estado
          abierto el cierre es una salida discreta (ya no lo necesita). */}
      {ticket.status === PENDING_CLOSURE_STATUS ? (
        <div className={`rounded-2xl border-2 p-6 shadow-sm ${isDarkMode ? "border-emerald-500/50 bg-emerald-500/10" : "border-emerald-300 bg-emerald-50"}`}>
          <h2 className={`text-base font-bold ${isDarkMode ? "text-white" : "text-gray-900"}`}>¿Quedó resuelto?</h2>
          <p className={`mt-1 text-sm ${isDarkMode ? "text-white/70" : "text-gray-600"}`}>
            Marcamos tu ticket como resuelto. Confírmalo para cerrarlo o dinos si el problema sigue.
          </p>
          {closureError && (
            <div className={`mt-3 rounded-lg px-3 py-2 text-sm ${isDarkMode ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-700"}`}>{closureError}</div>
          )}
          {closureMode === "rework" ? (
            <div className="mt-4 space-y-3">
              <label className={`block text-xs font-medium ${isDarkMode ? "text-white/70" : "text-gray-600"}`} htmlFor="sd-rework-reason">
                ¿Qué no quedó resuelto? *
              </label>
              <textarea
                id="sd-rework-reason"
                rows={3}
                autoFocus
                className={isDarkMode ? darkInputCls : lightInputCls}
                placeholder="Describe qué sigue fallando para que podamos rehacerlo."
                value={reworkReason}
                onChange={(e) => setReworkReason(e.target.value)}
              />
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={onRequestRework}
                  disabled={closureBusy || !isValidReworkReason(reworkReason)}
                  className="rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
                  style={{ backgroundColor: statusFallback("rework")?.color }}
                >
                  {closureBusy ? "Enviando…" : "Enviar a rehacer"}
                </button>
                <button
                  type="button"
                  onClick={() => { setClosureMode("idle"); setClosureError(null); }}
                  disabled={closureBusy}
                  className={secondaryBtnCls}
                >
                  Cancelar
                </button>
              </div>
            </div>
          ) : (
            <div className="mt-4 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={onCloseTicket}
                disabled={closureBusy}
                className="rounded-full px-5 py-2 text-sm font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: brand.primary }}
              >
                {closureBusy ? "Cerrando…" : "Sí, cerrar ticket"}
              </button>
              <button
                type="button"
                onClick={() => { setClosureMode("rework"); setClosureError(null); }}
                disabled={closureBusy}
                className={secondaryBtnCls}
              >
                No quedó resuelto (rehacer)
              </button>
            </div>
          )}
        </div>
      ) : ticket.status !== CLOSED_STATUS ? (
        <div className="text-right">
          {closureError && (
            <div className={`mb-2 rounded-lg px-3 py-2 text-left text-sm ${isDarkMode ? "bg-red-900/20 text-red-300" : "bg-red-50 text-red-700"}`}>{closureError}</div>
          )}
          {closureMode === "confirmClose" ? (
            <div className={`inline-flex flex-wrap items-center justify-end gap-3 rounded-lg border px-4 py-3 text-sm ${isDarkMode ? "border-dark-border bg-dark-surface text-white" : "border-gray-200 bg-white text-gray-700"}`}>
              <span>¿Cerrar este ticket? Ya no se seguirá trabajando en él.</span>
              <button
                type="button"
                onClick={onCloseTicket}
                disabled={closureBusy}
                className="rounded-full px-4 py-1.5 text-sm font-semibold text-white disabled:opacity-60"
                style={{ backgroundColor: brand.primary }}
              >
                {closureBusy ? "Cerrando…" : "Sí, cerrar"}
              </button>
              <button
                type="button"
                onClick={() => { setClosureMode("idle"); setClosureError(null); }}
                disabled={closureBusy}
                className={`text-sm underline ${isDarkMode ? "text-white/70" : "text-gray-500"}`}
              >
                Cancelar
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setClosureMode("confirmClose"); setClosureError(null); }}
              className={`text-xs underline ${isDarkMode ? "text-white/60" : "text-gray-500"}`}
            >
              Cerrar ticket (ya no lo necesito)
            </button>
          )}
        </div>
      ) : null}

      <div className={`rounded-2xl border p-6 shadow-sm ${isDarkMode ? "border-dark-border bg-dark-surface" : "border-gray-200 bg-white"}`}>
        <h2 className={`mb-4 text-sm font-semibold ${isDarkMode ? "text-white" : "text-gray-700"}`}>Conversación</h2>
        <CommentThread comments={comments} onReply={onReply} replyLabel="Escribe una respuesta o adjunta evidencias…" />
      </div>
    </div>
  );
}

const lightInputCls = "w-full rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200";
const darkInputCls = "w-full rounded-lg border border-dark-border bg-dark-card px-3 py-2 text-sm text-white focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-200";
